// Öğün planı düzenleyicisi — "Her gün aynı" (1 gün) veya haftalık (7 gün).
// Düz editör dili (kutusuz bölümler, drawer ile ekleme): bkz. PatientWorkspaceTabs/_editorParts.

import React, { useMemo, useState } from "react";
import { Box, IconButton, Typography, alpha } from "@mui/material";
import {
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";

import { COLORS, TYPO } from "../../../styles/uiConstants";
import { ActionButton, ChoiceChipGroup, FormTextField } from "../../atoms";
import EmptyState from "../../atoms/EmptyState";
import TagPickerDrawer from "../PatientWorkspaceTabs/drawers/TagPickerDrawer";
import { makeFetchPage, pickFoods, foodOption } from "../PatientWorkspaceTabs/drawers/fetchers";
import { useListFoodsMutation } from "../../../store/systemVariablesApi";
import {
  MEAL_TYPES,
  MEAL_LABEL,
  dayTotals,
  mealTotals,
  itemFromCatalog,
  rescaleItem,
  toWeekly,
  toDaily,
} from "./dietPlanFormat";

const MODE_OPTIONS = [
  { value: "daily", label: "Her gün aynı" },
  { value: "weekly", label: "Haftalık" },
];

const smallInputSx = { "& input": { fontSize: TYPO.body, py: 0.25 } };

// Boş öğün planının (illüstrasyonlu boş durum) yüksekliği — öğün alanı bunun altına inmez
const MEALS_MIN_HEIGHT = 280;

// Besin satırı sütunları: ad | miktar | birim | enerji | sil
const ITEM_GRID = { xs: "1fr 72px 56px 70px 32px", md: "1fr 90px 64px 90px 32px" };

const fmt = (v) => Math.round(Number(v) || 0).toLocaleString("tr-TR");
/** "P 12 g · K 30 g · Y 5 g" — değeri olanlar */
const macroLine = (it) =>
  [
    it.protein != null && it.protein !== "" ? `P ${fmt(it.protein)} g` : null,
    it.carbs != null && it.carbs !== "" ? `K ${fmt(it.carbs)} g` : null,
    it.fat != null && it.fat !== "" ? `Y ${fmt(it.fat)} g` : null,
  ]
    .filter(Boolean)
    .join(" · ");

function TotalsLine({ totals, targets }) {
  const part = (label, value, target, unit) => {
    const over = target && value > target * 1.1;
    return (
      <Typography component="span" sx={{ fontSize: TYPO.label, color: COLORS.slate500, fontVariantNumeric: "tabular-nums" }}>
        {label}{" "}
        <Box component="span" sx={{ color: over ? COLORS.warningDark : COLORS.slate800, fontWeight: 500 }}>
          {Math.round(value).toLocaleString("tr-TR")}
        </Box>
        {target ? ` / ${Number(target).toLocaleString("tr-TR")}` : ""} {unit}
      </Typography>
    );
  };
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}>
      {part("Enerji", totals.calories, Number(targets?.calories) || null, "kcal")}
      {part("Protein", totals.protein, Number(targets?.protein) || null, "g")}
      {part("Karbonhidrat", totals.carbs, Number(targets?.carbs) || null, "g")}
      {part("Yağ", totals.fat, Number(targets?.fat) || null, "g")}
    </Box>
  );
}

export default function MealPlanEditor({ days, onChange, targets, canEdit, palette, token }) {
  const [listFoods] = useListFoodsMutation();
  const fetchFoods = useMemo(() => makeFetchPage(listFoods, token, pickFoods, foodOption), [listFoods, token]);

  const mode = (days || []).length === 7 ? "weekly" : "daily";
  const safeDays = (days || []).length ? days : [{ label: "Her gün", meals: [] }];
  const [dayIdx, setDayIdx] = useState(0);
  const idx = Math.min(dayIdx, safeDays.length - 1);
  const day = safeDays[idx];

  // Besin ekleme drawer'ı hangi öğün için açık
  const [pickerMeal, setPickerMeal] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const setDay = (nextDay) => onChange(safeDays.map((d, i) => (i === idx ? nextDay : d)));
  const setMeal = (mi, nextMeal) => setDay({ ...day, meals: day.meals.map((m, i) => (i === mi ? nextMeal : m)) });

  const usedTypes = new Set((day.meals || []).map((m) => m.type));
  const missingTypes = MEAL_TYPES.filter((t) => !usedTypes.has(t.value));

  const addMeal = (type) => {
    const def = MEAL_TYPES.find((t) => t.value === type);
    const meals = [...(day.meals || []), { type, time: def?.time || "", note: "", items: [] }];
    meals.sort((a, b) => MEAL_TYPES.findIndex((t) => t.value === a.type) - MEAL_TYPES.findIndex((t) => t.value === b.type));
    setDay({ ...day, meals });
  };

  const changeMode = (next) => {
    if (next === mode) return;
    onChange(next === "weekly" ? toWeekly(safeDays) : toDaily(safeDays));
    setDayIdx(0);
  };

  const copyDayToWeek = () => onChange(safeDays.map((d) => ({ ...d, meals: day.meals.map((m) => ({ ...m, items: [...m.items] })) })));

  const totals = dayTotals(day);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <ChoiceChipGroup
          options={MODE_OPTIONS}
          value={mode}
          onChange={changeMode}
          disabled={!canEdit}
          palette={palette}
          ariaLabel="Plan düzeni"
        />
        {mode === "weekly" && canEdit && (
          <ActionButton
            variant="edit"
            compact
            palette={palette}
            icon={<CopyIcon sx={{ fontSize: 16 }} />}
            label="Bu günü tüm haftaya kopyala"
            onClick={copyDayToWeek}
          />
        )}
      </Box>

      {mode === "weekly" && (
        <ChoiceChipGroup
          options={safeDays.map((d, i) => ({ value: String(i), label: d.label || `Gün ${i + 1}` }))}
          value={String(idx)}
          onChange={(v) => setDayIdx(Number(v))}
          palette={palette}
          ariaLabel="Gün"
        />
      )}

      <Box sx={{ py: 1, borderBottom: `1px solid ${COLORS.slate100}` }}>
        <TotalsLine totals={totals} targets={targets} />
      </Box>

      {/* Öğün alanı: boşken görünen yükseklik hep ayrılır — ilk öğün eklenince alan
          daralıp zıplamaz; öğünler bu yüksekliği aşınca aşağı doğru büyür. */}
      <Box sx={{ minHeight: MEALS_MIN_HEIGHT, display: "flex", flexDirection: "column", gap: 1.5 }}>
      {(day.meals || []).length === 0 ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <EmptyState
            variant="illustrated-sm"
            icon={<RestaurantIcon />}
            title="Henüz öğün eklenmedi"
            description={canEdit ? "Aşağıdan öğün ekleyin; besinleri listeden seçin." : "Bu planda öğün yok."}
          />
        </Box>
      ) : (
        day.meals.map((meal, mi) => {
          const mt = mealTotals(meal);
          return (
            <Box
              key={meal.type}
              sx={{ border: `1px solid ${COLORS.slate200}`, borderRadius: 2, overflow: "hidden" }}
            >
              {/* Öğün başlığı: tema renginde soluk bant + solda vurgu çizgisi */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  px: 1.5,
                  py: 0.75,
                  bgcolor: alpha(palette?.primary || COLORS.slate500, 0.06),
                  boxShadow: `inset 3px 0 0 ${palette?.primary || COLORS.slate500}`,
                }}
              >
                <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: palette?.primary || COLORS.slate800, minWidth: 80 }}>
                  {MEAL_LABEL[meal.type]}
                </Typography>
                <FormTextField
                  palette={palette}
                  variant="standard"
                  type="time"
                  value={meal.time || ""}
                  onChange={(e) => setMeal(mi, { ...meal, time: e.target.value })}
                  disabled={!canEdit}
                  sx={{ width: 96, ...smallInputSx }}
                  inputProps={{ "aria-label": `${MEAL_LABEL[meal.type]} saati` }}
                />
                <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    bgcolor: COLORS.white,
                    border: `1px solid ${COLORS.slate200}`,
                    fontSize: TYPO.label,
                    color: COLORS.slate700,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {mt.calories.toLocaleString("tr-TR")} kcal
                </Box>
                <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                  {canEdit && (
                    <>
                      <ActionButton
                        variant="add"
                        compact
                        palette={palette}
                        label="Besin ekle"
                        onClick={() => {
                          setPickerMeal(mi);
                          setPickerOpen(true);
                        }}
                      />
                      <IconButton
                        size="small"
                        aria-label={`${MEAL_LABEL[meal.type]} öğününü kaldır`}
                        onClick={() => setDay({ ...day, meals: day.meals.filter((_, i) => i !== mi) })}
                        sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>

              {(meal.items || []).length === 0 ? (
                <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate400, px: 1.5, py: 1 }}>Besin yok</Typography>
              ) : (
                <>
                {/* Sütun başlıkları */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: ITEM_GRID,
                    gap: 1,
                    px: 1.5,
                    pt: 0.75,
                    pb: 0.25,
                    fontSize: TYPO.caption,
                    color: COLORS.slate400,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  <span>Besin</span>
                  <span>Miktar</span>
                  <span>Birim</span>
                  <Box component="span" sx={{ textAlign: "right" }}>Enerji</Box>
                  <span />
                </Box>
                {meal.items.map((it, ii) => (
                  <Box
                    key={`${it.food || it.name}-${ii}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: ITEM_GRID,
                      alignItems: "center",
                      gap: 1,
                      py: 0.5,
                      px: 1.5,
                      // Dönüşümlü hafif zemin: satırlar göz ile takip edilebilsin
                      bgcolor: ii % 2 === 1 ? COLORS.slate50 : "transparent",
                      "&:hover": { bgcolor: alpha(palette?.primary || COLORS.slate500, 0.05) },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap title={it.name} sx={{ fontSize: TYPO.body, color: COLORS.slate800 }}>
                        {it.name}
                      </Typography>
                      {macroLine(it) ? (
                        <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate400, fontVariantNumeric: "tabular-nums" }}>
                          {macroLine(it)}
                        </Typography>
                      ) : null}
                    </Box>
                    <FormTextField
                      palette={palette}
                      variant="standard"
                      type="number"
                      value={it.amount ?? ""}
                      onChange={(e) =>
                        setMeal(mi, {
                          ...meal,
                          items: meal.items.map((x, k) => (k === ii ? rescaleItem(x, e.target.value) : x)),
                        })
                      }
                      disabled={!canEdit}
                      sx={smallInputSx}
                      inputProps={{ min: 0, "aria-label": `${it.name} miktarı` }}
                    />
                    <Typography noWrap sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
                      {it.amountUnit || ""}
                    </Typography>
                    <Typography
                      sx={{ fontSize: TYPO.label, color: COLORS.slate500, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                    >
                      {it.calories != null && it.calories !== "" ? `${Math.round(it.calories)} kcal` : "—"}
                    </Typography>
                    {canEdit ? (
                      <IconButton
                        size="small"
                        aria-label={`${it.name} kaldır`}
                        onClick={() => setMeal(mi, { ...meal, items: meal.items.filter((_, k) => k !== ii) })}
                        sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    ) : (
                      <span />
                    )}
                  </Box>
                ))}
                </>
              )}
            </Box>
          );
        })
      )}

      </Box>

      {canEdit && missingTypes.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>Öğün ekle:</Typography>
          <ChoiceChipGroup
            options={missingTypes.map((t) => ({ value: t.value, label: t.label }))}
            value={null}
            onChange={addMeal}
            palette={palette}
            ariaLabel="Öğün ekle"
          />
        </Box>
      )}

      <TagPickerDrawer
        open={pickerOpen && pickerMeal != null}
        onClose={() => setPickerOpen(false)}
        icon={<RestaurantIcon />}
        title={pickerMeal != null && day.meals[pickerMeal] ? `${MEAL_LABEL[day.meals[pickerMeal].type]} · Besin ekle` : "Besin ekle"}
        subtitle="Besinler katalogdaki porsiyonla eklenir; miktarı sonra değiştirebilirsiniz."
        fetchPage={fetchFoods}
        existing={(pickerMeal != null && day.meals[pickerMeal] ? day.meals[pickerMeal].items : []).map((f) => f.name)}
        searchPlaceholder="Besin ara"
        noun="besin"
        onSubmit={(_, options) => {
          const meal = day.meals[pickerMeal];
          if (meal) {
            setMeal(pickerMeal, { ...meal, items: [...meal.items, ...options.map((o) => itemFromCatalog(o.raw))] });
          }
          setPickerOpen(false);
        }}
      />
    </Box>
  );
}
